import { useState, useEffect, useMemo, useRef } from 'react';
import {
  getCustomCalibreById,
  getVariantsForParent,
  getImageUrl,
} from '../../services/firebase';
import { ReferenceCard } from './ReferenceCard';
import { ReferenceBulkActionBar, applyDotPathUpdates } from './ReferenceBulkActionBar';
import type { WatchBrand, WatchReference } from '../../types';
import toast from 'react-hot-toast';
import { Button } from '../common/Button';
import { Modal } from '../common/Modal';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { pushParentCalibresToVariants } from './parentCalibrePush';
import {
  describeBlock,
  planVariantCalibrePush,
  type VariantCalibrePushPlan,
} from './variantCalibrePush';

/** Rows per page in table AND cards view. Typical parents never see the control. */
const VARIANTS_PAGE_SIZE = 25;

/** Global (per browser profile) cards-vs-table preference. */
const VIEW_MODE_KEY = 'referenceEditor:variants:viewMode';

/**
 * Per-parent expand memory. Only written while expanded and REMOVED on collapse,
 * so localStorage only ever holds keys for parents the admin left open and prunes
 * itself instead of growing one key per parent ever visited. Brand-scoped because
 * reference doc ids are only unique within a brand's subcollection.
 */
const expandedKey = (brandId: string, parentId: string) =>
  `referenceEditor:variants:expanded:${brandId}:${parentId}`;

type VariantsViewMode = 'cards' | 'table';

function readViewMode(): VariantsViewMode {
  try {
    return localStorage.getItem(VIEW_MODE_KEY) === 'table' ? 'table' : 'cards';
  } catch {
    // Safari private mode throws on localStorage access.
    return 'cards';
  }
}

function readExpanded(brandId: string, parentId: string): boolean {
  try {
    return localStorage.getItem(expandedKey(brandId, parentId)) === '1';
  } catch {
    return false;
  }
}

/** Dial style lives at the top level of a reference, not under dialAndHands. */
function dialStyleOf(ref: WatchReference): string | undefined {
  return ref.dialStyle;
}

/**
 * The status flags a reference actually carries. `specialEdition` and
 * `knownDiscontinuation` are the only booleans on WatchReference — `hidden` /
 * `needsReview` are brand-level and don't exist here. Missing imagery is derived
 * rather than stored, and is the failure mode worth surfacing on a variant.
 */
function flagsOf(ref: WatchReference): { label: string; className: string }[] {
  const flags: { label: string; className: string }[] = [];
  if (ref.specialEdition) {
    flags.push({ label: 'Special ed.', className: 'bg-amber-50 text-amber-700 border-amber-200' });
  }
  if (ref.knownDiscontinuation) {
    flags.push({ label: 'Discontinued', className: 'bg-gray-100 text-gray-600 border-gray-200' });
  }
  if (!ref.images || ref.images.length === 0) {
    flags.push({ label: 'No image', className: 'bg-red-50 text-red-600 border-red-200' });
  }
  return flags;
}

// ── VariantRow ────────────────────────────────────────────────────────────────

interface VariantRowProps {
  reference: WatchReference;
  selected: boolean;
  onSelect: () => void;
  onOpen: () => void;
}

function VariantRow({ reference, selected, onSelect, onOpen }: VariantRowProps) {
  const [imageUrl, setImageUrl] = useState<string | null>(null);

  useEffect(() => {
    if (reference.images && reference.images[0]?.storagePath) {
      getImageUrl(reference.images[0].storagePath)
        .then(setImageUrl)
        .catch(() => setImageUrl(null));
    } else {
      setImageUrl(null);
    }
  }, [reference.images]);

  const flags = flagsOf(reference);

  return (
    <tr
      className={`hover:bg-gray-50 cursor-pointer border-b border-gray-100 ${selected ? 'bg-blue-50' : ''}`}
      onClick={onOpen}
    >
      <td className='px-3 py-2 w-8' onClick={(e) => { e.stopPropagation(); onSelect(); }}>
        <input
          type='checkbox'
          checked={selected}
          onChange={() => {}}
          className='w-4 h-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500 cursor-pointer'
        />
      </td>
      <td className='px-3 py-2 w-14'>
        {imageUrl ? (
          <img src={imageUrl} alt={reference.reference} className='w-10 h-10 object-cover rounded' />
        ) : (
          <div className='w-10 h-10 bg-gray-100 rounded flex items-center justify-center'>
            <svg className='w-5 h-5 text-gray-300' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
              <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={1} d='M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z' />
            </svg>
          </div>
        )}
      </td>
      <td className='px-3 py-2 font-medium text-sm text-gray-900 whitespace-nowrap'>
        {reference.reference}
      </td>
      <td className='px-3 py-2 text-sm text-gray-600 whitespace-nowrap'>
        {reference.dialAndHands?.color || '—'}
      </td>
      <td className='px-3 py-2 text-sm text-gray-600 whitespace-nowrap'>
        {dialStyleOf(reference) || '—'}
      </td>
      <td className='px-3 py-2 text-sm text-gray-600 whitespace-nowrap'>
        {reference.strap?.material || '—'}
      </td>
      <td className='px-3 py-2 text-sm text-gray-600 whitespace-nowrap'>
        {reference.case?.material || '—'}
      </td>
      <td className='px-3 py-2 whitespace-nowrap'>
        {flags.length === 0 ? (
          <span className='text-sm text-gray-300'>—</span>
        ) : (
          <div className='flex items-center gap-1'>
            {flags.map(({ label, className }) => (
              <span
                key={label}
                className={`inline-flex items-center px-1.5 py-0.5 rounded border text-[10px] font-medium ${className}`}
              >
                {label}
              </span>
            ))}
          </div>
        )}
      </td>
    </tr>
  );
}

// ── VariantsPanel ─────────────────────────────────────────────────────────────

export interface VariantsPanelProps {
  brand: WatchBrand;
  /** The PARENT reference whose variants this panel manages. */
  parent: WatchReference;
  /** How many refs point at `parent`. The panel renders nothing when 0. */
  variantCount: number;
  /**
   * Open a variant in the editor. Routed through the caller's unsaved-changes
   * guard; absent → variant rows/cards aren't clickable.
   */
  onOpenReference?: (reference: WatchReference) => void;
}

/**
 * The parent's variants, managed in place on its editor page.
 *
 * Collapsed by default with the count in the header, so the child docs are only
 * fetched when an admin actually opens the section (and, on a later visit, when
 * they'd left it open). Cards or table, select-all-visible, paginated, and the
 * same bulk toolbar the brand listing uses minus the Algolia resync — variants
 * aren't indexed, so resyncing them syncs nothing.
 */
export function VariantsPanel({
  brand,
  parent,
  variantCount,
  onOpenReference,
}: VariantsPanelProps) {
  const [expanded, setExpanded] = useState(() => readExpanded(brand.id, parent.id));
  const [viewMode, setViewMode] = useState<VariantsViewMode>(readViewMode);

  const [variants, setVariants] = useState<WatchReference[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [page, setPage] = useState(0);
  // Guards against a slow fetch for a previous parent overwriting a newer one.
  const fetchReqId = useRef(0);

  /**
   * "Push parent calibre" — the Bulk Calibre Enricher's Apply Parent Calibre to
   * Variants, for this one parent. Planned on click (one calibre read), shown as
   * a preview, written only on confirm. Same dual-write flags as the enricher.
   */
  const dualWriteLegacyMovement = useFeatureFlag('calibre.dualWriteLegacyMovement');
  const dualWriteLegacyCalibreString = useFeatureFlag('calibre.dualWriteLegacyCalibreString');
  const [pushPlan, setPushPlan] = useState<VariantCalibrePushPlan | null>(null);
  const [pushCalibreName, setPushCalibreName] = useState('');
  const [pushPlanning, setPushPlanning] = useState(false);
  const [pushing, setPushing] = useState(false);

  // Reset everything when the editor swaps to a different reference, and pick up
  // that parent's own remembered expand state.
  useEffect(() => {
    setVariants(null);
    setSelectedIds(new Set());
    setPage(0);
    setExpanded(readExpanded(brand.id, parent.id));
  }, [brand.id, parent.id]);

  const loadVariants = async () => {
    const reqId = ++fetchReqId.current;
    setLoading(true);
    try {
      const rows = await getVariantsForParent(brand.id, parent.id);
      if (reqId !== fetchReqId.current) return;
      setVariants(rows);
    } catch (err) {
      if (reqId !== fetchReqId.current) return;
      console.error('Failed to load variants:', err);
      toast.error('Failed to load variants');
    } finally {
      if (reqId === fetchReqId.current) setLoading(false);
    }
  };

  // Lazy fetch: first expand only (including an expand restored from
  // localStorage). Collapsing keeps the rows in memory, so re-expanding is free.
  useEffect(() => {
    if (expanded && variants === null && !loading) loadVariants();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded, variants]);

  const toggleExpanded = () => {
    setExpanded((prev) => {
      const next = !prev;
      try {
        if (next) localStorage.setItem(expandedKey(brand.id, parent.id), '1');
        else localStorage.removeItem(expandedKey(brand.id, parent.id));
      } catch {
        // Preference is best-effort; never block the toggle on storage.
      }
      // Collapsing hides the action bar, so drop the selection with it rather
      // than leaving one the admin can't see or clear. The fetched rows stay.
      if (!next) setSelectedIds(new Set());
      return next;
    });
  };

  const changeViewMode = (mode: VariantsViewMode) => {
    setViewMode(mode);
    try {
      localStorage.setItem(VIEW_MODE_KEY, mode);
    } catch {
      // Best-effort.
    }
  };

  const rows = variants ?? [];
  const totalPages = Math.max(1, Math.ceil(rows.length / VARIANTS_PAGE_SIZE));
  // Deleting the tail of the list can leave `page` past the end.
  const safePage = Math.min(page, totalPages - 1);
  const pageRows = useMemo(
    () => rows.slice(safePage * VARIANTS_PAGE_SIZE, (safePage + 1) * VARIANTS_PAGE_SIZE),
    [rows, safePage],
  );

  // Select-all covers the VISIBLE page, matching the brand listing (whose
  // select-all covers what's loaded and filtered). Paging or switching view keeps
  // the selection, so the toolbar count can exceed the current page.
  const allPageSelected =
    pageRows.length > 0 && pageRows.every((r) => selectedIds.has(r.id));
  const somePageSelected = pageRows.some((r) => selectedIds.has(r.id));

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAllOnPage = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allPageSelected) pageRows.forEach((r) => next.delete(r.id));
      else pageRows.forEach((r) => next.add(r.id));
      return next;
    });
  };

  const selectedRefs = useMemo(
    () => rows.filter((r) => selectedIds.has(r.id)),
    [rows, selectedIds],
  );

  const planPush = async () => {
    setPushPlanning(true);
    try {
      const calibreId = (parent.calibreId ?? '').trim();
      const entry = calibreId ? await getCustomCalibreById(calibreId) : null;
      setPushCalibreName(entry?.name ?? '');
      setPushPlan(
        planVariantCalibrePush({
          parent,
          variants: rows,
          entry,
          brand: { id: brand.id, name: brand.name },
        }),
      );
    } catch (err) {
      console.error('Failed to plan calibre push:', err);
      toast.error('Could not load the linked calibre');
    } finally {
      setPushPlanning(false);
    }
  };

  const confirmPush = async () => {
    if (!pushPlan || pushPlan.fixes.length === 0) return;
    setPushing(true);
    try {
      const runToken =
        typeof crypto !== 'undefined' && 'randomUUID' in crypto
          ? crypto.randomUUID()
          : `push-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      const { updatedCount } = await pushParentCalibresToVariants(
        brand.id,
        pushPlan.fixes,
        {
          legacyMovement: dualWriteLegacyMovement,
          legacyCalibreString: dualWriteLegacyCalibreString,
        },
        runToken,
      );
      toast.success(
        `Pushed ${pushCalibreName || 'the calibre'} to ${updatedCount} variant${updatedCount === 1 ? '' : 's'}`,
      );
      setPushPlan(null);
      await loadVariants();
    } catch (err) {
      console.error('Failed to push calibre to variants:', err);
      toast.error('Could not push the calibre to the variants');
    } finally {
      setPushing(false);
    }
  };

  // Not a parent → no panel at all, and (per ReferenceEditor) no fetch either.
  if (variantCount === 0) return null;

  return (
    <div className='bg-white shadow rounded-lg'>
      <button
        type='button'
        onClick={toggleExpanded}
        className='w-full p-4 flex items-center justify-between text-left'
      >
        <div className='flex items-center gap-2'>
          <h3 className='font-medium text-gray-900'>Variants</h3>
          <span className='inline-flex items-center px-2 py-0.5 rounded-full bg-brand-50 text-brand-700 border border-brand-200 text-xs font-semibold tabular-nums'>
            {/* Server count until the docs are loaded, then the loaded length —
                so the badge stays honest after a bulk delete in this session. */}
            {variants === null ? variantCount : variants.length}
          </span>
        </div>
        <svg
          className={`w-5 h-5 text-gray-500 transition-transform ${expanded ? 'rotate-180' : ''}`}
          fill='none'
          stroke='currentColor'
          viewBox='0 0 24 24'
        >
          <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M19 9l-7 7-7-7' />
        </svg>
      </button>

      {expanded && (
        <div className='px-4 pb-4 space-y-3'>
          <p className='text-sm text-gray-500'>
            Shopify product variants grouped under this reference. They aren&apos;t
            indexed for search and never appear in the brand&apos;s reference
            listing — this is where they&apos;re managed.
          </p>

          {loading && variants === null ? (
            <div className='flex justify-center py-8'>
              <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600' />
            </div>
          ) : rows.length === 0 ? (
            <div className='text-center py-6 text-sm text-gray-500'>
              No variants loaded. The count may be stale — reopen the reference to
              refresh it.
            </div>
          ) : (
            <>
              {/* Controls: select-all + view toggle */}
              <div className='flex items-center gap-2 flex-wrap'>
                <label className='flex items-center gap-1.5 cursor-pointer'>
                  <input
                    type='checkbox'
                    checked={allPageSelected}
                    ref={(el) => {
                      if (el) el.indeterminate = somePageSelected && !allPageSelected;
                    }}
                    onChange={toggleSelectAllOnPage}
                    className='w-4 h-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500 cursor-pointer'
                  />
                  <span className='text-xs text-gray-500'>
                    Select all {pageRows.length} shown
                  </span>
                </label>
                {selectedIds.size > 0 && (
                  <span className='text-xs text-brand-600 font-medium'>
                    ({selectedIds.size} selected)
                  </span>
                )}

                <div className='flex-1' />

                <Button
                  variant='secondary'
                  size='sm'
                  loading={pushPlanning}
                  disabled={pushPlanning || pushing}
                  onClick={() => void planPush()}
                  title="Fill variants that have no calibre with this reference's calibre (and tier)"
                >
                  Push calibre to variants
                </Button>

                <div className='flex border border-gray-200 rounded overflow-hidden'>
                  <button
                    type='button'
                    onClick={() => changeViewMode('cards')}
                    className={`p-1.5 ${viewMode === 'cards' ? 'bg-brand-50 text-brand-600' : 'text-gray-400 hover:text-gray-600'}`}
                    title='Card view'
                  >
                    <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                      <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z' />
                    </svg>
                  </button>
                  <button
                    type='button'
                    onClick={() => changeViewMode('table')}
                    className={`p-1.5 border-l border-gray-200 ${viewMode === 'table' ? 'bg-brand-50 text-brand-600' : 'text-gray-400 hover:text-gray-600'}`}
                    title='Table view'
                  >
                    <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                      <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M4 6h16M4 10h16M4 14h16M4 18h16' />
                    </svg>
                  </button>
                </div>
              </div>

              {viewMode === 'cards' ? (
                <div className='grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3'>
                  {pageRows.map((v) => (
                    <ReferenceCard
                      key={v.id}
                      reference={v}
                      variant
                      onClick={() => onOpenReference?.(v)}
                      selected={selectedIds.has(v.id)}
                      onSelect={() => toggleSelect(v.id)}
                    />
                  ))}
                </div>
              ) : (
                /* The editor's right column is 2/3 width, so the table scrolls
                   horizontally rather than squeezing its columns. */
                <div className='overflow-x-auto rounded-lg border border-gray-200'>
                  <table className='w-full text-left border-collapse'>
                    <thead>
                      <tr className='bg-gray-50 border-b border-gray-200'>
                        <th className='px-3 py-2 w-8'>
                          <input
                            type='checkbox'
                            checked={allPageSelected}
                            ref={(el) => {
                              if (el) el.indeterminate = somePageSelected && !allPageSelected;
                            }}
                            onChange={toggleSelectAllOnPage}
                            className='w-4 h-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500 cursor-pointer'
                          />
                        </th>
                        <th className='px-3 py-2 w-14' />
                        <th className='px-3 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider'>Reference</th>
                        <th className='px-3 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider'>Dial Colour</th>
                        <th className='px-3 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider'>Dial Style</th>
                        <th className='px-3 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider'>Strap</th>
                        <th className='px-3 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider'>Case Material</th>
                        <th className='px-3 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider'>Flags</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pageRows.map((v) => (
                        <VariantRow
                          key={v.id}
                          reference={v}
                          selected={selectedIds.has(v.id)}
                          onSelect={() => toggleSelect(v.id)}
                          onOpen={() => onOpenReference?.(v)}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {totalPages > 1 && (
                <div className='flex items-center justify-between'>
                  <span className='text-xs text-gray-500 tabular-nums'>
                    Showing {safePage * VARIANTS_PAGE_SIZE + 1}–
                    {Math.min((safePage + 1) * VARIANTS_PAGE_SIZE, rows.length)} of {rows.length}
                  </span>
                  <div className='flex items-center gap-1'>
                    <button
                      type='button'
                      onClick={() => setPage(Math.max(0, safePage - 1))}
                      disabled={safePage === 0}
                      className='px-2 py-1 text-xs border rounded disabled:opacity-30'
                    >
                      ←
                    </button>
                    <span className='text-xs text-gray-500 tabular-nums px-1'>
                      {safePage + 1} / {totalPages}
                    </span>
                    <button
                      type='button'
                      onClick={() => setPage(Math.min(totalPages - 1, safePage + 1))}
                      disabled={safePage >= totalPages - 1}
                      className='px-2 py-1 text-xs border rounded disabled:opacity-30'
                    >
                      →
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Bulk actions over the selected variants. Same actions as the brand
          listing minus Resync Algolia — variants aren't indexed there. */}
      {expanded && selectedIds.size > 0 && (
        <ReferenceBulkActionBar
          groupingActions="variants"
          brand={brand}
          selectedRefs={selectedRefs}
          onClearSelection={() => setSelectedIds(new Set())}
          showResync={false}
          onRefsUpdated={(ids, updates) =>
            setVariants((prev) =>
              (prev ?? []).map((r) =>
                ids.has(r.id)
                  ? applyDotPathUpdates(r as unknown as Record<string, unknown>, updates) as unknown as WatchReference
                  : r,
              ),
            )
          }
          onRefsDeleted={(ids) => {
            setVariants((prev) => (prev ?? []).filter((r) => !ids.has(r.id)));
            setSelectedIds(new Set());
          }}
          onRefsEnriched={loadVariants}
        />
      )}

      <Modal
        isOpen={pushPlan !== null}
        onClose={() => (pushing ? undefined : setPushPlan(null))}
        title='Push calibre to variants'
      >
        {pushPlan && (
          <div className='space-y-3'>
            {pushPlan.blocked ? (
              <p className='text-sm text-amber-800'>{describeBlock(pushPlan.blocked)}</p>
            ) : pushPlan.fixes.length === 0 ? (
              <p className='text-sm text-gray-700'>
                Nothing to do — all {rows.length} variant{rows.length === 1 ? '' : 's'} already
                have a calibre, and existing calibres are never overwritten.
              </p>
            ) : (
              <>
                <p className='text-sm text-gray-700'>
                  Fills <strong>{pushPlan.fixes.length}</strong> of {rows.length} variant
                  {rows.length === 1 ? '' : 's'} with <strong>{pushCalibreName}</strong>
                  {pushPlan.tierName ? (
                    <>
                      {' '}
                      · tier <strong>{pushPlan.tierName}</strong>
                    </>
                  ) : null}
                  .
                </p>
                {pushPlan.alreadyHaveCalibre > 0 && (
                  <p className='text-xs text-gray-500'>
                    {pushPlan.alreadyHaveCalibre} already have a calibre and are left alone.
                  </p>
                )}
                <ul className='max-h-48 overflow-y-auto rounded border border-gray-200 bg-gray-50 px-2 py-1 font-mono text-xs text-gray-700'>
                  {pushPlan.fixes.map((fix) => (
                    <li key={fix.ref.id}>{fix.ref.reference || fix.ref.id}</li>
                  ))}
                </ul>
              </>
            )}
            <div className='flex justify-end gap-2'>
              <Button variant='secondary' onClick={() => setPushPlan(null)} disabled={pushing}>
                {pushPlan.fixes.length > 0 && !pushPlan.blocked ? 'Cancel' : 'Close'}
              </Button>
              {pushPlan.fixes.length > 0 && !pushPlan.blocked && (
                <Button onClick={() => void confirmPush()} loading={pushing} disabled={pushing}>
                  Push to {pushPlan.fixes.length} variant{pushPlan.fixes.length === 1 ? '' : 's'}
                </Button>
              )}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
